import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  CreateTenantAssetPayload,
  DeleteTenantAssetPayload,
  TenantAsset,
  TenantAssetResult,
  UpdateTenantAssetInput,
  UpdateTenantAssetPayload,
} from "../../schemas/tenantAssetSchema";

const GET_TENANT_ASSETS = gql`
  query GetTenantAssets($tenantId: ID!, $assetType: String) {
    tenantAssets(tenantId: $tenantId, assetType: $assetType) {
      assets {
        id
        tenantId
        assetType
        fileName
        fileUrl
        format
        thumbnailUrl
        metadata

        createdAt
        updatedAt
      }
    }
  }
`;

const GET_MY_TENANT_ASSETS = gql`
  query GetMyTenantAssets {
    myTenantAssets {
      assets {
        id
        tenantId
        assetType
        fileName
        fileUrl
        format
        thumbnailUrl
        metadata

        createdAt
        updatedAt
      }
    }
  }
`;

const GET_TENANT_ASSET_BY_ID = gql`
  query GetTenantAssetById($id: ID!) {
    tenantAsset(id: $id) {
      id
      tenantId
      assetType
      fileName
      fileUrl
      format
      thumbnailUrl
      metadata
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TENANT_ASSET = gql`
  mutation UpdateTenantAsset($id: ID!, $input: UpdateTenantAssetInput!) {
    updateTenantAsset(id: $id, input: $input)
  }
`;

const CREATE_TENANT_ASSET = gql`
  mutation CreateTenantAsset($input: CreateTenantAssetInput!) {
    createTenantAsset(input: $input)
  }
`;

const DELETE_TENANT_ASSET = gql`
  mutation DeleteTenantAsset($id: ID!) {
    deleteTenantAsset(id: $id)
  }
`;

export const tenantAssetService = {
  getByTenantId: async (
    tenantId: string,
    assetType?: "logo" | "background" | "widget" | "report_logo"
  ): Promise<TenantAssetResult> => {
    const { data } = await client.query({
      query: GET_TENANT_ASSETS,
      variables: { tenantId, assetType },
      fetchPolicy: "network-only",
    });
    return data.tenantAssets;
  },

  getMyAssets: async (): Promise<TenantAssetResult> => {
    const { data } = await client.query({
      query: GET_MY_TENANT_ASSETS,
      fetchPolicy: "network-only",
    });
    return data.myTenantAssets;
  },

  getById: async (id: string): Promise<TenantAsset> => {
    const { data } = await client.query({
      query: GET_TENANT_ASSET_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.tenantAsset;
  },

  update: async (payload: UpdateTenantAssetPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_TENANT_ASSET,
      variables: {
        id: payload.id,
        input: payload.input,
      },
      context: payload.input.file
        ? {
            headers: {
              "Apollo-Require-Preflight": "true",
            },
          }
        : undefined,
    });
    return data.updateTenantAsset;
  },

  create: async (payload: CreateTenantAssetPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_TENANT_ASSET,
      variables: {
        input: payload.input,
      },
      context: payload.input.file
        ? {
            headers: {
              "Apollo-Require-Preflight": "true",
            },
          }
        : undefined,
    });
    return data.createTenantAsset;
  },

  remove: async (payload: DeleteTenantAssetPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_TENANT_ASSET,
      variables: {
        id: payload.id,
      },
    });
    return data.deleteTenantAsset;
  },

};
